import {
  alignBarsByTime,
  computeRelativeStrength,
  relativeStrengthScore,
  RS_STUDY_METER_SCALE,
} from '../../utils/relativeStrength';

/**
 * Rolling relative-strength score at each bar, using only bars up to that bar.
 * @returns {Array<{ timeSec: number, closeTimeSec: number, time: string|number, score: number, scorePct: number, windowExcessPct: number }>}
 */
export function buildRsSeries({
  tickerBars,
  benchBars,
  timeframe,
  scale = RS_STUDY_METER_SCALE,
} = {}) {
  if (!timeframe || !Array.isArray(tickerBars) || !Array.isArray(benchBars)) return [];
  const minBars = timeframe.minBars || 10;
  const barsback = timeframe.range === 'ytd' ? Infinity : (timeframe.barsback || minBars);
  const aligned = alignBarsByTime(tickerBars, benchBars, Infinity);
  if (aligned.length < minBars) return [];

  const points = [];
  for (let i = minBars - 1; i < aligned.length; i += 1) {
    const start = Number.isFinite(barsback) ? Math.max(0, i + 1 - barsback) : 0;
    let windowStart = start;
    if (timeframe.range === 'ytd') {
      const year = yearOf(aligned[i].ticker);
      windowStart = i;
      while (windowStart > 0 && yearOf(aligned[windowStart - 1].ticker) === year) windowStart -= 1;
    }
    const window = aligned.slice(windowStart, i + 1);
    if (window.length < minBars) continue;
    const result = computeRelativeStrength({
      tickerBars: window.map((pair) => pair.ticker),
      benchmarkBars: window.map((pair) => pair.benchmark),
      unit: timeframe.unit,
      minBars,
      barsback: window.length,
    });
    if (!result.ok) continue;
    const score = relativeStrengthScore(result.scorePct, scale);
    if (!Number.isFinite(score)) continue;
    const last = window[window.length - 1].ticker;
    const timeSec = Number.isFinite(last.timeSec)
      ? last.timeSec
      : (typeof last.time === 'number' ? last.time : null);
    if (!Number.isFinite(timeSec)) continue;
    points.push({
      timeSec,
      closeTimeSec: last.closeTimeSec ?? timeSec,
      time: last.time,
      score,
      scorePct: result.scorePct,
      windowExcessPct: result.windowExcessPct,
    });
  }
  return points;
}

/** Mean RS at each timestamp across tickers that have a score that day. */
export function averageRsSeries(seriesList) {
  const byTime = new Map();
  (seriesList || []).forEach((series) => {
    (series || []).forEach((point) => {
      if (!Number.isFinite(point?.timeSec) || !Number.isFinite(point?.score)) return;
      const bucket = byTime.get(point.timeSec) || {
        timeSec: point.timeSec,
        closeTimeSec: point.closeTimeSec ?? point.timeSec,
        time: point.time,
        scores: [],
      };
      bucket.scores.push(point.score);
      byTime.set(point.timeSec, bucket);
    });
  });
  return [...byTime.values()]
    .sort((a, b) => a.timeSec - b.timeSec)
    .map((bucket) => ({
      timeSec: bucket.timeSec,
      closeTimeSec: bucket.closeTimeSec,
      time: bucket.time,
      score: bucket.scores.reduce((sum, value) => sum + value, 0) / bucket.scores.length,
      n: bucket.scores.length,
    }));
}

/** Price path indexed to 100 at the first close. */
export function indexedCloses(bars) {
  const valid = (bars || []).filter((bar) => (
    Number.isFinite(bar?.timeSec) && Number.isFinite(bar?.close) && bar.close > 0
  ));
  if (!valid.length) return [];
  const base = valid[0].close;
  return valid.map((bar) => ({
    timeSec: bar.timeSec,
    closeTimeSec: bar.closeTimeSec ?? bar.timeSec,
    value: (bar.close / base) * 100,
  }));
}

/** Percent change from an index series that starts at 100. */
export function gainFromIndex(points) {
  return (points || [])
    .filter((point) => Number.isFinite(point?.timeSec) && Number.isFinite(point?.value))
    .map((point) => ({
      timeSec: point.timeSec,
      gain: point.value - 100,
    }));
}

/** Equal-weight basket indexed to 100 from each ticker’s first close. */
export function equalWeightIndex(barsByTicker) {
  const bases = {};
  Object.entries(barsByTicker || {}).forEach(([ticker, bars]) => {
    const first = (bars || []).find((bar) => Number.isFinite(bar?.close) && bar.close > 0);
    if (first) bases[ticker] = first.close;
  });
  const byTime = new Map();
  Object.entries(barsByTicker || {}).forEach(([ticker, bars]) => {
    const base = bases[ticker];
    if (!base) return;
    (bars || []).forEach((bar) => {
      if (!Number.isFinite(bar?.timeSec) || !Number.isFinite(bar?.close) || bar.close <= 0) return;
      const bucket = byTime.get(bar.timeSec) || { timeSec: bar.timeSec, values: [] };
      bucket.values.push((bar.close / base) * 100);
      byTime.set(bar.timeSec, bucket);
    });
  });
  return [...byTime.values()]
    .sort((a, b) => a.timeSec - b.timeSec)
    .map((bucket) => ({
      timeSec: bucket.timeSec,
      value: bucket.values.reduce((sum, value) => sum + value, 0) / bucket.values.length,
      n: bucket.values.length,
    }));
}

function yearOf(bar) {
  if (typeof bar?.time === 'string' && bar.time.length >= 4) return bar.time.slice(0, 4);
  const sec = Number(bar?.timeSec);
  if (!Number.isFinite(sec)) return '';
  return new Date(sec * 1000).toISOString().slice(0, 4);
}
